/** Sales & Menu Mix module — sales, average check, combo attach, top items + day-part forecast. */
import React from 'react';
import { DollarSign, Receipt, Layers, Trash2, Sparkles, CalendarClock, Users } from 'lucide-react';
import { toast } from 'sonner';
import KpiTile from './KpiTile';
import Heatmap from './Heatmap';
import AskCorvana from './AskCorvana';
import CoryTips from '@/components/demo/CoryTips';
import { QSR_STORES, DAYPARTS, NETWORK_FORECAST, avgBy, money } from './qsrDemoData';

export default function SalesMenuMixModule({ forecastEnabled = true, onNavigate }) {
  const totalSales = QSR_STORES.reduce((s, x) => s + x.weeklySales, 0);
  const netCheck = avgBy('avgCheck');
  const netCombo = avgBy('comboAttach');
  const netWaste = avgBy('wastePct');
  const heat = QSR_STORES.map((s) => s.salesByDaypart);

  const agg = {};
  QSR_STORES.forEach((s) => s.topItems.forEach((it) => { agg[it.name] = (agg[it.name] || 0) + it.pct; }));
  const items = Object.entries(agg).map(([name, pct]) => ({ name, pct: Math.round(pct / QSR_STORES.length) }))
    .sort((a, b) => b.pct - a.pct).slice(0, 5);
  const maxItem = Math.max(...items.map((i) => i.pct));
  const maxFc = Math.max(...NETWORK_FORECAST.map((f) => f.predicted));
  const peak = NETWORK_FORECAST.reduce((a, b) => (b.predicted > a.predicted ? b : a));

  return (
    <div className="space-y-4 qsr-stage p-3 sm:p-5" data-testid="sales-module">
      <CoryTips page="sales" variant="qsr" onAction={onNavigate} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={DollarSign} label="Network weekly sales" value={money(totalSales)} sub="3 stores" delta="+4.2%" deltaGood color="#10b981" trend={QSR_STORES[0].salesTrend} testid="sales-kpi-total" source="Lightspeed POS · vs ATO retail-food benchmark" ai="Sales are up 4.2% on last week, led by Footscray's dinner day-part. October typically ramps ~14% — worth locking in extra dinner crew now." />
        <KpiTile icon={Receipt} label="Average check" source="Lightspeed POS" value={`$${netCheck.toFixed(2)}`} sub="per order" delta="+$0.40" deltaGood color="#3b82f6" />
        <KpiTile icon={Layers} label="Combo attachment" source="Lightspeed POS · items/order" value={`${netCombo.toFixed(0)}%`} sub="upsell rate" delta="+3%" deltaGood color="#8b5cf6" ai={`Combo attach at ${netCombo.toFixed(0)}% is your fastest margin lever. Southport (48%) is dragging the network — a scripted POS upsell closes most of the gap.`} />
        <KpiTile icon={Trash2} label="Waste %" source="Lightspeed · vs 2.5% benchmark" value={`${netWaste.toFixed(1)}%`} sub="target <2.5%" delta="-0.3%" deltaGood color="#f59e0b" />
      </div>

      {forecastEnabled && (
        <div className="rounded-2xl qsr-card p-4 shadow-sm" data-testid="sales-forecast">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-blue-50 border border-blue-200"><CalendarClock className="w-4 h-4 text-blue-600" /></div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Day-part sales forecast · tomorrow</div>
                <div className="text-xs text-slate-500">Roster ahead of the {peak.daypart.toLowerCase()} peak</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toast.success('Roster sent to Tanda', { description: `Recommended crew per day-part pushed to rostering. (Demo — connect Tanda to go live.)` })} className="inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold px-3 py-1.5 bg-sky-600 text-white hover:bg-sky-700 transition-colors" data-testid="forecast-roster-push">
                <Users className="w-3.5 h-3.5" /> Send roster to Tanda
              </button>
              <AskCorvana insight={`Tomorrow's ${peak.daypart.toLowerCase()} is forecast at $${peak.predicted}k — ${Math.round((peak.predicted / peak.typical - 1) * 100)}% above a typical day. Roster ${peak.recStaff} crew to hold speed of service under target.`} action="Push roster to Tanda" />
            </div>
          </div>
          <div className="mt-3 grid grid-cols-7 gap-2 items-end" style={{ minHeight: 140 }}>
            {NETWORK_FORECAST.map((f) => {
              const isPeak = f.daypart === peak.daypart;
              return (
                <div key={f.daypart} className="flex flex-col items-center gap-1.5" data-testid={`forecast-${f.daypart.toLowerCase()}`}>
                  <span className="text-[11px] font-semibold text-slate-800">${f.predicted}k</span>
                  <div className="w-full flex items-end justify-center gap-0.5" style={{ height: 90 }}>
                    <div className="w-2.5 rounded-t bg-slate-200" style={{ height: `${(f.typical / maxFc) * 100}%` }} title={`typical $${f.typical}k`} />
                    <div className={`w-2.5 rounded-t transition-[height] duration-700 ${isPeak ? 'bg-blue-600' : 'bg-blue-400'}`} style={{ height: `${(f.predicted / maxFc) * 100}%` }} title={`forecast $${f.predicted}k`} />
                  </div>
                  <span className={`text-[10px] ${isPeak ? 'text-blue-700 font-semibold' : 'text-slate-500'}`}>{f.daypart}</span>
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500"><Users className="w-2.5 h-2.5" />{f.recStaff}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-500">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500" /> forecast</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-300" /> typical</span>
            <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> recommended crew</span>
          </div>
        </div>
      )}

      <Heatmap
        title="Sales share by day-part (% of daily sales · higher is better)"
        rows={QSR_STORES.map((s) => s.name)}
        cols={DAYPARTS}
        values={heat}
        unit="%"
        ai="Dinner is your biggest day-part across every store. Southport skews even more to dinner (32%) — protect speed and staffing there first."
      />

      <div className="rounded-2xl qsr-card p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-900">Top selling items (network mix)</div>
          <AskCorvana insight="The Classic Grill Burger drives ~20% of mix. Bundling it into the dinner combo would lift attachment and average check together." />
        </div>
        <div className="space-y-2.5">
          {items.map((it) => (
            <div key={it.name} className="flex items-center gap-3">
              <span className="w-44 text-xs text-slate-700 truncate">{it.name}</span>
              <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-[width] duration-700" style={{ width: `${(it.pct / maxItem) * 100}%` }} />
              </div>
              <span className="w-9 text-right text-xs font-semibold text-slate-800">{it.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
